import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Info, ChevronDown, ChevronUp, Zap, ShieldCheck } from "lucide-react";
import { AdminConsentWizard } from "@/components/AdminConsentWizard";
import { AccountForm } from "@/components/accounts/AccountForm";
import { AccountCard } from "@/components/accounts/AccountCard";

const empty = {
  label: "",
  provider: "gmail",
  email_address: "",
  username: "",
  password: "",
  imap_host: "",
  imap_port: "",
  smtp_host: "",
  smtp_port: "",
  smtp_ssl: true,
  immobiliare_url: "",
  website_url: "",
  send_as: "",
};

export default function Accounts() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);  // null = create mode, string = editing this account
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [oauthConfigured, setOauthConfigured] = useState(false);
  const [oauthBusy, setOauthBusy] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/accounts");
      setList(data);
    } catch (err) {
      console.error("Accounts: failed to load /accounts", err?.message || err);
    }
  }, []);

  const loadOauthStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/oauth/microsoft/status");
      setOauthConfigured(!!data.configured);
    } catch (err) {
      console.error("Accounts: failed to load oauth status", err?.message || err);
    }
  }, []);

  useEffect(() => {
    load();
    loadOauthStatus();
  }, [load, loadOauthStatus]);

  // Listen for popup postMessage after Microsoft OAuth completes
  useEffect(() => {
    const handler = (event) => {
      const data = event?.data;
      if (!data || typeof data !== "object") return;
      if (data.type === "microsoft-oauth-success") {
        toast.success(data.email ? `Outlook collegato: ${data.email}` : "Outlook collegato");
        setOauthBusy(false);
        load();
      } else if (data.type === "microsoft-admin-consent-required") {
        setOauthBusy(false);
        toast.info("Account aziendale: serve il consenso admin. Apro la procedura guidata…");
        setWizardOpen(true);
      } else if (data.type === "microsoft-admin-consent-success") {
        toast.success("Consenso admin concesso! Ora puoi collegare la casella.");
        setWizardOpen(true);
      } else if (data.type === "microsoft-admin-consent-error") {
        toast.error(data.message || "Consenso admin non completato");
      } else if (data.type === "microsoft-oauth-error") {
        toast.error(data.message || "Errore OAuth Microsoft");
        setOauthBusy(false);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [load]);

  const connectMicrosoft = async () => {
    if (!oauthConfigured) {
      toast.error("OAuth Microsoft non configurato sul server");
      return;
    }
    // IMPORTANT: open the popup SYNCHRONOUSLY inside the click handler (with a placeholder URL)
    // so the browser does not block it as a popup. Then, once the backend responds with the
    // Microsoft authorization URL, we redirect the popup to it.
    const w = 600;
    const h = 700;
    const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
    const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
    const popup = window.open(
      "about:blank",
      "microsoft-oauth",
      `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no`
    );
    if (!popup || popup.closed || typeof popup.closed === "undefined") {
      toast.error("Popup bloccato dal browser. Consenti i popup per questo sito e riprova.");
      return;
    }
    // Show a loading state inside the placeholder popup (safe DOM APIs, no document.write)
    try {
      const doc = popup.document;
      doc.body.style.cssText =
        "font-family:-apple-system,sans-serif;text-align:center;padding:40px;background:#F7F5F0;color:#5C5B5A;";
      const heading = doc.createElement("h2");
      heading.style.cssText = "font-family:serif;font-weight:300;";
      heading.textContent = "Connessione a Microsoft…";
      const msg = doc.createElement("p");
      msg.textContent = "Attendere qualche secondo";
      doc.body.append(heading, msg);
    } catch (err) {
      console.debug("Accounts: popup placeholder skipped", err?.message || err);
    }

    setOauthBusy(true);
    try {
      const { data } = await api.post("/oauth/microsoft/authorize");
      // Redirect popup to Microsoft consent page
      popup.location.href = data.authorization_url;
      // Watch for popup being closed manually
      const interval = setInterval(() => {
        if (popup.closed) {
          clearInterval(interval);
          setOauthBusy(false);
        }
      }, 500);
    } catch (err) {
      try {
        popup.close();
      } catch (closeErr) {
        console.debug("Accounts: popup close failed", closeErr?.message || closeErr);
      }
      setOauthBusy(false);
      toast.error(err?.response?.data?.detail || "Errore avvio OAuth");
    }
  };

  const oauthDisconnect = async (id) => {
    if (!window.confirm("Scollegare l'account Microsoft? Dovrai riautorizzare per riprendere l'accesso.")) return;
    try {
      await api.post(`/accounts/${id}/oauth/disconnect`);
      toast.success("Account scollegato");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const startEdit = (acct) => {
    setForm({
      label: acct.label || "",
      provider: acct.provider || "gmail",
      email_address: acct.email_address || "",
      username: acct.username || "",
      password: "",  // empty = keep existing on save
      imap_host: acct.imap_host || "",
      imap_port: acct.imap_port ? String(acct.imap_port) : "",
      smtp_host: acct.smtp_host || "",
      smtp_port: acct.smtp_port ? String(acct.smtp_port) : "",
      smtp_ssl: acct.smtp_ssl !== false,
      immobiliare_url: acct.immobiliare_url || "",
      website_url: acct.website_url || "",
      send_as: acct.send_as || "",
    });
    setEditingId(acct.id);
    setShowForm(true);
    // scroll to form
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
  };

  const cancelEdit = () => {
    setForm(empty);
    setEditingId(null);
    setShowForm(false);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.username) payload.username = payload.email_address;
      ["imap_port", "smtp_port"].forEach((k) => {
        payload[k] = payload[k] ? parseInt(payload[k], 10) : undefined;
      });
      ["imap_host", "smtp_host"].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      if (editingId) {
        // omit empty password to preserve existing
        if (!payload.password) delete payload.password;
        await api.put(`/accounts/${editingId}`, payload);
        toast.success("Account aggiornato");
      } else {
        await api.post("/accounts", payload);
        toast.success("Account aggiunto");
      }
      setForm(empty);
      setEditingId(null);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo account?")) return;
    await api.delete(`/accounts/${id}`);
    toast.success("Account rimosso");
    load();
  };

  const test = async (id) => {
    const t = toast.loading("Test connessione…");
    try {
      const { data } = await api.post(`/accounts/${id}/test`);
      toast.dismiss(t);
      if (data.ok) toast.success(data.message);
      else toast.error(data.message);
      load();
    } catch (err) {
      toast.dismiss(t);
      console.error("Accounts: account test failed", err?.message || err);
      toast.error("Errore test");
    }
  };

  const toggleAcct = async (id, enabled) => {
    await api.post(`/accounts/${id}/toggle`, { enabled });
    load();
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-mono">Account email</div>
          <h1 className="font-display text-4xl mt-2 font-light">Caselle gestite</h1>
          <p className="text-sm text-[#5C5B5A] mt-2 max-w-xl">
            Aggiungi le caselle Gmail o Outlook dell&apos;agenzia. Le credenziali sono cifrate e usate solo per leggere e rispondere alle email.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {oauthConfigured && (
            <Button
              data-testid="connect-microsoft-button"
              onClick={connectMicrosoft}
              disabled={oauthBusy}
              className="bg-[#0078D4] hover:bg-[#106EBE] text-white font-mono uppercase text-xs tracking-[0.2em]"
            >
              <Zap size={14} className="mr-2" />
              {oauthBusy ? "Connessione…" : "Connetti Outlook (OAuth2)"}
            </Button>
          )}
          {oauthConfigured && (
            <Button
              data-testid="admin-consent-button"
              onClick={() => setWizardOpen(true)}
              variant="outline"
              className="border-[#0078D4] text-[#0078D4] hover:bg-[#E7F1FC] font-mono uppercase text-xs tracking-[0.2em]"
            >
              <ShieldCheck size={14} className="mr-2" />
              Consenso admin M365
            </Button>
          )}
          <Button
            data-testid="add-account-button"
            onClick={() => {
              if (showForm) cancelEdit();
              else setShowForm(true);
            }}
            className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-mono uppercase text-xs tracking-[0.2em]"
          >
            <Plus size={14} className="mr-2" /> {showForm ? "Annulla" : "Aggiungi account"}
          </Button>
        </div>
      </div>

      {oauthConfigured && (
        <div className="border border-[#0078D4] bg-[#E7F1FC] p-4 flex items-start gap-3" data-testid="oauth-info-banner">
          <Zap size={18} className="text-[#0078D4] mt-0.5 flex-shrink-0" />
          <div className="text-sm text-[#1C1B1A] leading-relaxed">
            <strong>Novità:</strong> per Outlook, Hotmail e outlook.it usa il pulsante <em>&ldquo;Connetti Outlook (OAuth2)&rdquo;</em> qui sopra.
            Autorizzi la Segretaria direttamente sull&apos;account Microsoft (senza password app, senza IMAP da abilitare a mano).
            <br />
            <strong>Account aziendali Microsoft 365</strong> (es. <code>info@tuaagenzia.com</code>): serve un&apos;approvazione una tantum dell&apos;amministratore. Clicca <em>&ldquo;Consenso admin M365&rdquo;</em> per la procedura guidata (se l&apos;admin sei tu, si completa in 2 minuti).
          </div>
        </div>
      )}

      <AdminConsentWizard open={wizardOpen} onOpenChange={setWizardOpen} onConnect={connectMicrosoft} />

      {/* APP PASSWORD HELP — collapsible */}
      <div className="border border-[#E5E4E0] bg-white" data-testid="app-password-help">
        <button
          type="button"
          onClick={() => setShowHelp((v) => !v)}
          data-testid="toggle-help-button"
          className="w-full flex items-center justify-between px-5 py-3 hover:bg-[#F0EFEA] transition-colors text-left"
        >
          <span className="flex items-center gap-2">
            <Info size={16} className="text-[#2C4C3B]" />
            <span className="label-mono">Info gestione mail</span>
          </span>
          {showHelp ? <ChevronUp size={16} className="text-[#5C5B5A]" /> : <ChevronDown size={16} className="text-[#5C5B5A]" />}
        </button>
        {showHelp && (
          <div className="border-t border-[#E5E4E0] bg-[#FDF1EB] border-l-4 border-l-[#C85A32] p-5">
            <p className="text-sm text-[#1C1B1A] leading-relaxed">
              <strong>Gmail e Outlook NON accettano la password normale del tuo account</strong> per IMAP/SMTP.
              Devi creare una <em>App Password</em> dedicata di 16 caratteri.
            </p>
            <ul className="text-sm text-[#1C1B1A] mt-3 space-y-1.5 list-disc list-inside">
              <li>
                <strong>Gmail:</strong> attiva la verifica in 2 passaggi su{" "}
                <a className="underline text-[#2C4C3B]" href="https://myaccount.google.com/security" target="_blank" rel="noreferrer">myaccount.google.com/security</a>, poi vai su{" "}
                <a className="underline text-[#2C4C3B]" href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">myaccount.google.com/apppasswords</a>, crea una password chiamata &ldquo;Segretaria AI&rdquo; e incollala qui (anche senza spazi).
              </li>
              <li>
                <strong>Outlook / Office 365:</strong> attiva la 2FA su{" "}
                <a className="underline text-[#2C4C3B]" href="https://account.microsoft.com/security" target="_blank" rel="noreferrer">account.microsoft.com/security</a>, poi crea una password app da{" "}
                <a className="underline text-[#2C4C3B]" href="https://account.live.com/proofs/AppPassword" target="_blank" rel="noreferrer">account.live.com/proofs/AppPassword</a>. Verifica che IMAP sia abilitato (Outlook web → Impostazioni → Posta → Sincronizza email → POP e IMAP).
              </li>
            </ul>
          </div>
        )}
      </div>

      {showForm && (
        <AccountForm
          form={form}
          setForm={setForm}
          editingId={editingId}
          busy={busy}
          onSubmit={save}
          onCancel={cancelEdit}
        />
      )}

      {/* LIST */}
      <div className="space-y-3">
        {list.length === 0 && (
          <div className="border border-dashed border-[#E5E4E0] p-12 text-center text-sm text-[#8C8B8A]">
            Nessun account collegato. Aggiungi la tua prima casella.
          </div>
        )}
        {list.map((a) => (
          <AccountCard
            key={a.id}
            account={a}
            onToggle={toggleAcct}
            onEdit={startEdit}
            onTest={test}
            onDisconnect={oauthDisconnect}
            onDelete={remove}
          />
        ))}
      </div>
    </div>
  );
}
